/**
 * Grocery "In Pantry" matching (Grocery.jsx).
 *
 * Grocery rows carry the canonical Core IDs they were built from
 * (`core_food_ids`). A Core Pantry item whose ID is on this list matches by
 * that ID only, so a saved "Chicken Breast" no longer marks "Chicken Broth".
 * Everything else keeps the original name matching: older and custom Pantry
 * items, Core items whose ID isn't on this list, and grocery rows that carry
 * no Core IDs (those still match every Pantry item by name).
 */
var PANTRY_SECTIONS = ['proteins', 'pantry', 'dairy', 'produce', 'other'];

function pantryItemName(item) {
  // Safety check: only valid string names take part in name matching
  if (item && item.name && typeof item.name === 'string') {
    return item.name.toLowerCase().trim();
  }
  return null;
}

/** Lowercased names of every Pantry item, across the five sections. */
export function pantryItemNames(pantryData) {
  var names = [];
  PANTRY_SECTIONS.forEach(function(section) {
    ((pantryData && pantryData[section]) || []).forEach(function(item) {
      var name = pantryItemName(item);
      if (name !== null) names.push(name);
    });
  });
  return names;
}

/** The original Grocery name matcher (fuzzy), unchanged. */
export function nameMatchesAny(groceryItemName, pantryItems) {
  if (!pantryItems || pantryItems.length === 0) return false;
  var groceryLower = (groceryItemName || '').toLowerCase().trim();
  if (!groceryLower) return false;

  for (var i = 0; i < pantryItems.length; i++) {
    var pantryItem = pantryItems[i];
    // Skip if pantry item is not a valid string
    if (!pantryItem || typeof pantryItem !== 'string') continue;

    // Check for substring match in either direction
    if (groceryLower.includes(pantryItem) || pantryItem.includes(groceryLower)) {
      return true;
    }
    // Also check individual words for partial matching
    var groceryWords = groceryLower.split(/\s+/);
    var pantryWords = pantryItem.split(/\s+/);
    for (var j = 0; j < groceryWords.length; j++) {
      for (var k = 0; k < pantryWords.length; k++) {
        if (groceryWords[j] && pantryWords[k] &&
            groceryWords[j].length > 3 && pantryWords[k].length > 3 &&
            (groceryWords[j].includes(pantryWords[k]) || pantryWords[k].includes(groceryWords[j]))) {
          return true;
        }
      }
    }
  }
  return false;
}

/**
 * Returns isPantryMatch(groceryRow) for this Pantry and grocery list.
 *
 * A Pantry item is "ID-resolved" when it is a Core item (`source: 'core'`)
 * whose food_id appears in some row's core_food_ids. A row with Core IDs
 * matches through an ID-resolved item, otherwise by name against the Pantry
 * items that are not ID-resolved. A row without Core IDs matches by name
 * against every Pantry item, exactly as before.
 */
export function buildPantryMatcher(pantryData, groceryItems) {
  var listIds = new Set();
  (groceryItems || []).forEach(function(row) {
    ((row && row.core_food_ids) || []).forEach(function(id) {
      if (id) listIds.add(String(id));
    });
  });

  var resolvedIds = new Set();
  var allNames = [];
  var fallbackNames = [];
  PANTRY_SECTIONS.forEach(function(section) {
    ((pantryData && pantryData[section]) || []).forEach(function(item) {
      var resolved = !!item && item.source === 'core' && !!item.food_id &&
        listIds.has(String(item.food_id));
      if (resolved) resolvedIds.add(String(item.food_id));
      var name = pantryItemName(item);
      if (name === null) return;
      allNames.push(name);
      if (!resolved) fallbackNames.push(name);
    });
  });

  return function isPantryMatch(row) {
    if (!row) return false;
    var ids = row.core_food_ids || [];
    for (var i = 0; i < ids.length; i++) {
      if (ids[i] && resolvedIds.has(String(ids[i]))) return true;
    }
    return nameMatchesAny(row.display || row.name, ids.length > 0 ? fallbackNames : allNames);
  };
}
